import _, {chain} from 'lodash';

import {teammateCanAccessInbox, teammateCanMoveToInbox} from '../../../helpers/modelHelpers';
import {
  ConversationTrackerStagesEnum,
  ConversationTrackerStatusesEnum,
} from '../../../models/api/conversationTrackerModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {ThunkAction} from '../../appThunk';
import {getHasNewMoveToUnreachable} from '../../featureFlags/featureFlagsSelectors';
import {ConversationUndoBannerTypesEnum} from '../../panels/app/banners/bannersState';
import {CellsContext} from '../../panels/cells/context/cellsContext';
import {InboxesParameters} from '../../panels/inboxes/parameters/inboxesParameters';
import {getChannels} from '../channels/channelsSelectors';
import {getCanMoveToInaccessibleInbox} from '../company/companySelectors';
import {getConversations} from '../dataSelectors';
import {getDelegatedInboxIdsSet} from '../delegations/delegationsSelectors';
import {getInboxes} from '../inboxes/inboxesSelectors';
import {getFindTeammate} from '../teammates/lazyTeammatesSelectors';
import {getUser, getUserId} from '../teammates/teammatesSelectors';
import {updateConversationsBulkAction} from './bulkConversationsActions';
import {makeGetTrackers} from './conversationsSelectors';
import {updateConversationsAction} from './updateConversationsActions';

export enum MoveToInboxSourceTypesEnum {
  WORKSPACE_INBOX_DROP = 'workspace-inbox-drop',
  LEGACY_API = 'legacy-api',
  PLUGIN_API = 'plugin-api',
  SIDE_FRAME = 'side-frame',
  DROPDOWN = 'dropdown',
}

interface MoveToInboxSourceBase {
  type: MoveToInboxSourceTypesEnum;
}

interface MoveToInboxSourceSimple extends MoveToInboxSourceBase {
  type:
    | MoveToInboxSourceTypesEnum.WORKSPACE_INBOX_DROP
    | MoveToInboxSourceTypesEnum.LEGACY_API
    | MoveToInboxSourceTypesEnum.PLUGIN_API;
}

interface MoveToInboxSourceSideFrame extends MoveToInboxSourceBase {
  type: MoveToInboxSourceTypesEnum.SIDE_FRAME;
  endpoint: string;
}

export interface MoveToInboxSourceDropdown extends MoveToInboxSourceBase {
  type: MoveToInboxSourceTypesEnum.DROPDOWN;
  isOpenedByShortcut: boolean;
  queryText: string;
  isInboxUnreachable: boolean;
}

export type MoveToInboxSource =
  | MoveToInboxSourceSimple
  | MoveToInboxSourceSideFrame
  | MoveToInboxSourceDropdown;

/* Checks whether the current user can move to the given inbox. */
export function canUserMoveToInboxAction(inbox: InboxModel): ThunkAction<boolean> {
  return (_dispatch, getState) => {
    const state = getState();
    const user = getUser(state);
    const delegatedInboxIdsSet = getDelegatedInboxIdsSet(state);
    const canMoveToInaccessibleInbox = getCanMoveToInaccessibleInbox(state);

    return teammateCanMoveToInbox(user, inbox, delegatedInboxIdsSet, canMoveToInaccessibleInbox);
  };
}

export function isInboxUnreachableToTrackersAction(
  conversationIds: ReadonlyArray<number>,
  inboxId: number,
): ThunkAction<{
  isUnreachable: boolean;
  isUserOnlyFollower: boolean;
}> {
  return (_dispatch, getState) => {
    const state = getState();

    const allInboxes = getInboxes(state);
    const destinationInbox = allInboxes[inboxId];

    if (!destinationInbox) {
      return {
        isUnreachable: false,
        isUserOnlyFollower: false,
      };
    }

    const allConversations = getConversations(state);
    const getTrackers = makeGetTrackers(() =>
      _(conversationIds)
        .map((id) => allConversations[id])
        .compact()
        .value(),
    );
    const allTrackers = getTrackers(state, {});

    return {
      isUnreachable: allTrackers.some((t) => !teammateCanAccessInbox(t.teammate, destinationInbox)),
      isUserOnlyFollower: allTrackers.length === 1 && allTrackers[0].teammate.id === getUserId(state),
    };
  };
}

export function moveToInboxAction(
  teammateId: number,
  conversationIds: ReadonlyArray<number>,
  inboxId: number,
  source: MoveToInboxSource,
  shouldRetainAccess?: boolean,
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const inboxes = getInboxes(state);
    const channels = getChannels(state);
    const findTeammate = getFindTeammate(state);
    const hasNewMoveToUnreachable = getHasNewMoveToUnreachable(state);

    // Find the target inbox.
    const inbox = inboxes[inboxId];
    if (!inbox) {
      return null;
    }

    const conversationPatch =
      hasNewMoveToUnreachable && _.isBoolean(shouldRetainAccess)
        ? {inboxId, retainTrackerAccessOnMove: shouldRetainAccess}
        : {inboxId};

    return dispatch(
      updateConversationsAction(
        conversationIds,
        conversationPatch,
        (conversation) => {
          const conversationOwnerNamespaces = chain(conversation.channelIds)
            .map((id) => channels[id])
            .compact()
            .map((c) => c.namespace)
            .compact()
            .value();

          const newTrackers = chain(conversation.trackers)
            .map((tracker) => {
              // Find the teammate for this tracker.
              const teammate = tracker && findTeammate(tracker.teammateId, 'moveToInboxAction');
              if (!tracker || !teammate) {
                return null;
              }

              // Check if it should be removed.
              const shouldRemoveTracker =
                !teammateCanAccessInbox(teammate, inbox) &&
                !conversationOwnerNamespaces.includes(teammate.namespace);

              // Force archive for our teammate so the conversation will go away.
              const isTeammate = teammate.id === teammateId;
              const newStatus = (isTeammate && ConversationTrackerStatusesEnum.ARCHIVED) || tracker.status;

              if (!shouldRemoveTracker) {
                return {...tracker, status: newStatus};
              }

              return {...tracker, stage: ConversationTrackerStagesEnum.NONE};
            })
            .compact()
            .keyBy((tr) => tr.teammateId)
            .value();

          return {
            ...conversation,
            inboxIds: [inbox.id],
            trackers: newTrackers,
            namespace: inbox.namespace,
          };
        },
        {
          apiMetaData: {source},
          undo: hasNewMoveToUnreachable
            ? {
                type: ConversationUndoBannerTypesEnum.MOVE,
              }
            : undefined,
        },
      ),
    );
  };
}

export function bulkMoveToInboxAction(
  inboxesParameters: InboxesParameters,
  cellsContext: CellsContext,
  inboxId: number,
): ThunkAction {
  const conversationPatch = {
    inboxId,
  };
  return updateConversationsBulkAction(inboxesParameters, cellsContext, conversationPatch);
}
